// ✅ src/pages/VehicleV2/BatteryCardListing.jsx  (FULL FILE)
// Backend mount:
//   app.use("/api/batteryCardListing", batteryCardListing);
//
// Endpoints used:
//   GET  https://nissi-india.com/api/batteryCardListing/dropdowns?branchId=..
//   GET  https://nissi-india.com/api/batteryCardListing/list?branchId=..&page=1&limit=10&batteryType=..&batteryMake=..&batteryCode=..&serialNo=..&purchaseFrom=..&search=..
//   POST https://nissi-india.com/api/batteryCardListing/delete   body: { branchId, loginUserId, ids:[] }
//
// ✅ Notes:
// - UI styles kept same pattern (HeaderBar + VehicleLayoutWrapper + currentSkin)
// - Dropdowns loaded from backend (no hardcoded values)
// - Search button triggers fetch (same as ASPX Search click)
// - Pagination from backend totalPages
// - Select all works per current page
// - Sends fin-year headers if available (for new inventory-pool based backend)

import { useState, useContext, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaSearch, FaFilter, FaEdit, FaEye, FaTrash } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/batteryCardListing";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const BatteryCardListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // Session values
  const loginUserId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const branchId = Number(localStorage.getItem("branchid") || 8);

  // Optional financial year headers for new backend resolver
  const finYear =
    localStorage.getItem("finyear") ||
    localStorage.getItem("financialyear") ||
    sessionStorage.getItem("finyear") ||
    "";

  const finYearId =
    localStorage.getItem("finyearid") ||
    localStorage.getItem("financialyearid") ||
    sessionStorage.getItem("finyearid") ||
    "";

  const requestConfig = useMemo(() => {
    const headers = {};

    if (finYear) headers["x-finyear"] = finYear;
    if (finYearId) {
      headers["x-finyearid"] = finYearId;
      headers["x-finyear-id"] = finYearId;
    }

    return { headers };
  }, [finYear, finYearId]);

  // Dropdown data
  const [ddPurchaseFrom, setDdPurchaseFrom] = useState([]);
  const [ddBatteryTypes, setDdBatteryTypes] = useState([]);
  const [ddBatteryMakes, setDdBatteryMakes] = useState([]);

  // Filters
  const [batteryType, setBatteryType] = useState("");
  const [batteryMake, setBatteryMake] = useState("");
  const [batteryCode, setBatteryCode] = useState("");
  const [batterySerialNo, setBatterySerialNo] = useState("");
  const [purchaseFrom, setPurchaseFrom] = useState("");
  const [search, setSearch] = useState("");

  // Listing
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // UI
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // Paging
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const selectClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const loadDropdowns = useCallback(async () => {
    try {
      if (!branchId) return;

      const { data } = await axios.get(
        `${API_BASE}/dropdowns`,
        {
          params: { branchId },
          ...requestConfig,
        }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load dropdowns");
      }

      setDdPurchaseFrom(data.purchaseFrom || []);
      setDdBatteryTypes(data.batteryTypes || []);
      setDdBatteryMakes(data.batteryMakes || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Dropdowns error");
    }
  }, [branchId, requestConfig]);

  const loadList = useCallback(
    async (p = page) => {
      try {
        if (!branchId) {
          toast.warn("branchId not found in localStorage");
          return;
        }

        setLoading(true);

        const { data } = await axios.get(
          `${API_BASE}/list`,
          {
            params: {
              branchId,
              page: p,
              limit,
              batteryType,
              batteryMake,
              batteryCode,
              serialNo: batterySerialNo,
              purchaseFrom,
              search,
            },
            ...requestConfig,
          }
        );

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load list");
        }

        setRows(data.rows || []);
        setTotal(toInt(data.total, 0));
        setTotalPages(Math.max(1, toInt(data.totalPages, 1)));
        setPage(toInt(data.page, p));

        // reset selection after reload
        setSelectedIds([]);
        setSelectAll(false);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "List loading error");
        setRows([]);
        setTotal(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [
      branchId,
      page,
      limit,
      batteryType,
      batteryMake,
      batteryCode,
      batterySerialNo,
      purchaseFrom,
      search,
      requestConfig,
    ]
  );

  // Initial load
  useEffect(() => {
    if (branchId) {
      loadDropdowns();
    }
  }, [branchId, loadDropdowns]);

  useEffect(() => {
    if (branchId) {
      loadList(1);
    }
  }, [branchId, loadList]);

  // Selection helpers
  const currentPageRows = useMemo(() => rows, [rows]);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }

    setSelectedIds(currentPageRows.map((r) => r.id));
    setSelectAll(true);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (!rows.length) {
      setSelectAll(false);
      return;
    }
    const allSelected = rows.every((r) => selectedIds.includes(r.id));
    setSelectAll(allSelected);
  }, [selectedIds, rows]);

  const handleClearFilters = () => {
    setBatteryType("");
    setBatteryMake("");
    setBatteryCode("");
    setBatterySerialNo("");
    setPurchaseFrom("");
    setSearch("");
  };

  const handleSearchClick = () => {
    setPage(1);
    loadList(1);
  };

  const handleDelete = async () => {
    try {
      if (!selectedIds.length) {
        toast.warn("Please select at least one battery to delete!");
        return;
      }

      if (!branchId) {
        toast.warn("branchId missing");
        return;
      }

      const ok = window.confirm(
        `Delete ${selectedIds.length} battery card(s)?`
      );
      if (!ok) return;

      const { data } = await axios.post(
        `${API_BASE}/delete`,
        {
          branchId,
          loginUserId,
          ids: selectedIds,
        },
        requestConfig
      );

      if (!data?.success) {
        throw new Error(data?.message || "Delete failed");
      }

      if (data.blockedCount > 0) {
        toast.warn(
          `Deleted: ${data.deletedCount}, Blocked (in use): ${data.blockedCount}`
        );
      } else {
        toast.success("Deleted successfully");
      }

      const nextPage =
        rows.length === selectedIds.length && page > 1 ? page - 1 : page;

      loadList(nextPage);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete error");
    }
  };

  const handleEdit = (id) => {
    sessionStorage.setItem("BatteryID", String(id));
    navigate(`/vehicle/BatteryCardEntry?batteryId=${id}`);
  };

  const handleView = (id) => {
    sessionStorage.setItem("BatteryID", String(id));
    navigate(`/vehicle/BatteryCardEntry?view=1&batteryId=${id}`);
  };

  const handleAddNew = () => {
    sessionStorage.removeItem("BatteryID");
    navigate("/vehicle/BatteryCardEntry");
  };

  const pageNumbers = useMemo(() => {
    return Array.from({ length: totalPages }, (_, idx) => idx + 1);
  }, [totalPages]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BATTERY CARD</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Battery Cards
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters((prev) => !prev)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800${showFilters ? " bg-gray-800" : ""}`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50${showFilters ? " bg-gray-50" : ""}`
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
              >
                <FaPlus className="w-4 h-4" />
                Add New Battery Card
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="md:col-span-3">
                  <label className={labelClass}>Search Battery Cards</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaSearch className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Type to search battery cards..."
                      className={`pl-10 ${inputClass}`}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Battery Type</label>
                  <select
                    value={batteryType}
                    onChange={(e) => setBatteryType(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">Select</option>
                    {ddBatteryTypes.map((x) => (
                      <option key={x.id} value={x.name}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Battery Serial No</label>
                  <input
                    type="text"
                    value={batterySerialNo}
                    onChange={(e) => setBatterySerialNo(e.target.value)}
                    className={inputClass}
                    placeholder="Enter serial number"
                  />
                </div>

                <div>
                  <label className={labelClass}>Battery Code</label>
                  <input
                    type="text"
                    value={batteryCode}
                    onChange={(e) => setBatteryCode(e.target.value)}
                    className={inputClass}
                    placeholder="Enter battery code"
                  />
                </div>

                <div>
                  <label className={labelClass}>Battery Make</label>
                  <select
                    value={batteryMake}
                    onChange={(e) => setBatteryMake(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">Select</option>
                    {ddBatteryMakes.map((x) => (
                      <option key={x.id} value={x.name}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Purchase From</label>
                  <select
                    value={purchaseFrom}
                    onChange={(e) => setPurchaseFrom(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">Select</option>
                    {ddPurchaseFrom.map((x) => (
                      <option key={x.id} value={x.name}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleClearFilters}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                >
                  Clear All Filters
                </button>

                <button
                  onClick={handleSearchClick}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                >
                  <FaSearch />
                  Search Records
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold">S.No</th>
                    <th className="px-4 py-4 text-center font-semibold">Battery Code</th>
                    <th className="px-4 py-4 text-center font-semibold">Vehicle Reg No</th>
                    <th className="px-4 py-4 text-center font-semibold">Battery Type</th>
                    <th className="px-4 py-4 text-center font-semibold">Battery Make</th>
                    <th className="px-4 py-4 text-center font-semibold">Serial Number</th>
                    <th className="px-4 py-4 text-center font-semibold">Expected Life</th>
                    <th className="px-4 py-4 text-center font-semibold">Purchased From</th>
                    <th className="px-4 py-4 text-center font-semibold">Actions</th>
                    <th className="px-4 py-4 text-center font-semibold">
                      <div className="flex items-center justify-center">
                        Select All
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="ml-2"
                        />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: limit }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-800" : "bg-white"}>
                        {Array.from({ length: 10 }).map((__, cellIdx) => (
                          <td key={cellIdx} className="px-4 py-3">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={10}
                        className={`px-4 py-10 text-center ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        No Battery Cards Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={r.id}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 ${
                          isDark ? "bg-gray-900" : "bg-white"
                        }`}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white text-center">
                          {(page - 1) * limit + idx + 1}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                          {r.batteryCode}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                          {r.vehicleRegNo}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                          {r.batteryType}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                          {r.batteryMake}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                          {r.serialNumber}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                          {r.expectedLife}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                          {r.purchasedFrom}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => handleEdit(r.id)}
                              className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg"
                            >
                              <FaEdit />
                              Edit
                            </button>

                            <button
                              onClick={() => handleView(r.id)}
                              className="inline-flex items-center gap-1 px-3 py-2 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg"
                            >
                              <FaEye />
                              View
                            </button>
                          </div>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(r.id)}
                            onChange={() => toggleSelect(r.id)}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination + Delete */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Showing{" "}
              <span className="font-semibold">
                {total === 0 ? 0 : (page - 1) * limit + 1}
              </span>{" "}
              to{" "}
              <span className="font-semibold">
                {Math.min(page * limit, total)}
              </span>{" "}
              of <span className="font-semibold">{total}</span> results
            </div>

            <div className="flex items-center gap-4">
              {!loading && (
                <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 font-sans">
                  <button
                    onClick={() => {
                      const np = Math.max(page - 1, 1);
                      setPage(np);
                      loadList(np);
                    }}
                    disabled={page === 1}
                    className={`px-4 py-2 text-sm font-medium ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {pageNumbers.map((pg) => (
                    <button
                      key={pg}
                      onClick={() => {
                        setPage(pg);
                        loadList(pg);
                      }}
                      className={`px-4 py-2 text-sm font-medium transition ${
                        page === pg
                          ? `${buttonClass} text-white`
                          : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                      }`}
                    >
                      {pg}
                    </button>
                  ))}

                  <button
                    onClick={() => {
                      const np = Math.min(page + 1, totalPages);
                      setPage(np);
                      loadList(np);
                    }}
                    disabled={page === totalPages}
                    className={`px-4 py-2 text-sm font-medium ${
                      page === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}

              <button
                onClick={handleDelete}
                className={`flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg shadow transition ${
                  selectedIds.length === 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : `${buttonClass} ${hoverClass}`
                }`}
                disabled={selectedIds.length === 0}
              >
                <FaTrash /> Delete Selected
              </button>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BatteryCardListing;